import { yupResolver } from '@hookform/resolvers/yup';
import cls from 'classnames';
import React, { memo, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import styles from './OldDeviceStatus.module.css';
import Step from '../Step/Step';
import { ConfirmProps, StepType } from '../../../state/types';
import { State, useState } from '../../../state/store';
import QuestionTooltip from '../../QuestionTooltip/QuestionTooltip';
import FormContainer from '../../FormContainer/FormContainer';
import { oldDeviceStatusSchema } from '../../../state/validation';
import { getPurchasedDate } from '../../../utils/getPurchasedDate';
import useStateRef from 'react-usestateref';

interface DisabledFieldProps {
  id: string;
  label: string;
  value: string;
  inputClasses?: string;
}

const DisabledField = memo(({ id, label, value, inputClasses }: DisabledFieldProps) => {
  const fieldId = `inline-${id}`;

  return (
    <div className="md:flex md:items-center mb-6">
      <div className="md:w-1/2">
        <label className="block text-gray-500 font-bold md:text-right mb-1 md:mb-0 pr-4" htmlFor={fieldId}>
          {label}:
        </label>
      </div>
      <div className="md:w-1/2">
        <input
          value={value}
          disabled={true}
          className={cls(
            'appearance-none border-2 border-gray-200 rounded w-full py-2 px-2 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-blue-500',
            'disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none',
            inputClasses
          )}
          id={fieldId}
          type="text"
        />
      </div>
    </div>
  );
});

// Devices for which it is important working condition for trade-in device
// const qualifyingDevices = ['DM1', 'DM2', 'DM3'];

const OldDeviceStatus: React.FC = memo(() => {
  const {
    model,
    modelsMyBrand,
    tradeInValue,
    tradeInValueBad,
    imei,
    imeiOld,
    brand,
    isDeviceWorking,
    isFactoryResetDone,
    isVendorUnlocked,
    purchasedPhone,
    purchasedModel,
    purchaseDate,
    promoDevices,
    dispatch,
    eligibleDevices,
    promotions,
  } = useState(
    'model',
    'modelsMyBrand',
    'imeiOld',
    'brand',
    'imei',
    'tradeInValue',
    'tradeInValueBad',
    'isDeviceWorking',
    'isFactoryResetDone',
    'isVendorUnlocked',
    'purchasedModel',
    'purchasedPhone',
    'purchaseDate',
    'promoDevices',
    'eligibleDevices',
    'promotions'
  );
  const {
    register,
    trigger,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<State>({
    mode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(oldDeviceStatusSchema),
  });

  const [tradeInPromoValue, setTradeInPromoValue] = useStateRef(0);

  const prepareRegisterProps = useCallback(
    (name: keyof State, options = { required: true }) => {
      const registerProps = { ...register(name, options) };
      const originalOnChange = registerProps.onChange;

      registerProps.onChange = (e) => {
        /**
         * Trigger validation right when user is typing and update state in case there are no errors
         */
        return originalOnChange(e)
          .then(() => trigger(name))
          .then(() => {
            if (!errors[name]) {
              dispatch('update', {
                [name]: watch(name),
              });
            }
          });
      };
      return registerProps;
    },
    [register, dispatch, errors, trigger, watch]
  );

  const imeiOldRegisterProps = prepareRegisterProps('imeiOld');

  const Confirm = ({ name, value, text, isRequired }: ConfirmProps) => {
    setValue(name, value);
    const confirmProps = { ...prepareRegisterProps(name, { required: !!isRequired }) };
    return (
      <div
        className={'pb-3'}
        onClick={() => {
          const reversed = !value;
          dispatch('update', {
            [name]: reversed,
          });
        }}
      >
        <input type={'checkbox'} className={styles.checkbox} {...confirmProps} onChange={undefined} />
        <label className={cls('cursor-pointer inline', styles.label)}>{text}</label>
      </div>
    );
  };

  const modelOption = model ? modelsMyBrand.find((p) => p.value === model) : null;

  const isQualifyingDevice =
    //purchasedPhone && qualifyingDevices.includes(purchasedPhone) && brand !== 'Any Other Smartphone';
    purchasedPhone && brand !== 'Any Other Smartphone';

  const tradeInValueFinal = isQualifyingDevice
    ? isDeviceWorking && isVendorUnlocked
      ? tradeInValue
      : tradeInValueBad
    : '0.00';
  const tradeInValueTotal = +tradeInPromoValue + +tradeInValueFinal;
  const isOldImeiSame = imeiOld === imei;

  useEffect(() => {
    promoDevices.forEach((promoDevice) => {
      if (promoDevice.alias === purchasedPhone && promotions.is_enhancement_active) {
        setTradeInPromoValue(+promoDevice.promo_price);
        eligibleDevices.forEach((device) => {
          if (device.id === +model) {
            setTradeInPromoValue(+promoDevice.promo_price + +promoDevice.promo_price_enhancement);
          }
        });
        dispatch('update', {
          tradeinValueFinal: tradeInValueTotal,
        });
      }
    });
  }, [dispatch, setTradeInPromoValue, model, purchasedPhone, eligibleDevices, promoDevices, tradeInValueTotal]);

  return (
    <Step
      stepToShow={StepType.OldDeviceStatus}
      stepBack={StepType.OldDeviceModel}
      stepNext={StepType.Details}
      isValid={isValid && !isOldImeiSame}
    >
      <div className={styles.OldDeviceStatus} data-testid="OldDeviceStatus">
        <form className="w-full">
          <div className="bg-gray-100 rounded">
            <FormContainer header="Your Old Device">
              <DisabledField id="vendor" label="Vendor" value={brand} />
              <DisabledField id="model" label="Model Being Traded In" value={modelOption?.label ?? (model || '')} />
              {
                //purchasedPhone && qualifyingDevices.includes(purchasedPhone) && (
              }
              {purchasedPhone && (
                <>
                  <DisabledField id="model" label="* Estimated Trade-in Value" value={`€ ${tradeInValueFinal}`} />
                  <p>
                    * This is the estimated trade-in value of your old device and is subject to final inspection.
                    Subject to MacroEV T&C’s.
                  </p>
                </>
              )}
              <div className="md:flex md:items-start mt-12 mb-6">
                <div className="md:w-1/2">
                  <label
                    className="block text-gray-500 font-bold md:text-right mt-2 mb-1 md:mb-0 pr-4"
                    htmlFor="inline-imei-old"
                  >
                    Enter Old Trade-in Device IMEI:
                  </label>
                </div>
                <div className="md:w-1/2">
                  <div className={'flex'}>
                    <div className={styles.imeiInput}>
                      <input
                        {...imeiOldRegisterProps}
                        onBlur={() => trigger('imeiOld')}
                        defaultValue={imeiOld}
                        className="appearance-none border-2 border-gray-200 rounded w-full py-2 px-2 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-blue-500"
                        id="inline-imei-old"
                        type="text"
                      />
                      <div className="pt-2">
                        Please ensure all data is entered correctly before submitting the request
                      </div>
                      <div className="pt-2 text-red-400">
                        {errors.imeiOld?.message}
                        {isOldImeiSame && <div>Insert the IMEI of your OLD device to TRADE IN</div>}
                      </div>
                    </div>
                    <div className={cls(styles.imeiQuestion)}>
                      <QuestionTooltip>
                        <p className="p-2 m-0">
                          Dial <strong>*#06#</strong> from your device
                        </p>
                        <p className="p-2 m-0">An IMEI is 15 character long</p>
                        <p className="p-2 m-0">
                          Alternatively, Go to <strong>Settings</strong>, Select <strong>About phone</strong> or{' '}
                          <strong>About device</strong>
                        </p>
                        <p className="p-2 m-0">
                          On Apple device: Go to <strong>Settings</strong>, Select <strong>General</strong>, Select{' '}
                          <strong>About</strong> and scroll down.
                        </p>
                        <p className="p-2 m-0">
                          <strong>
                            Otherwise, IMEI is located underneath the battery or inscribed on the back of the mobile.
                          </strong>
                        </p>
                      </QuestionTooltip>
                    </div>
                  </div>
                </div>
              </div>
              {isQualifyingDevice && brand !== 'Any Other Smartphone' && (
                <div className="md:flex md:items-start mb-6">
                  <div className="md:w-1/3 mb-3">
                    <label
                      className="block text-gray-500 font-bold md:text-right mb-1 md:mb-0 pr-4"
                      htmlFor="inline-imei"
                    >
                      Checklist:
                    </label>
                  </div>
                  <div className={cls('md:w-2/3', styles.confirms)}>
                    <Confirm
                      name={'isDeviceWorking'}
                      value={isDeviceWorking}
                      text={
                        'The device is in working order, no signs of damage, discolouration, coloured hues or image retention.'
                      }
                    />
                    <Confirm
                      name={'isVendorUnlocked'}
                      value={isVendorUnlocked}
                      text={'All PIN, FMI, iCloud and Google locks are removed from device.'}
                    />
                    <Confirm
                      name={'isFactoryResetDone'}
                      value={isFactoryResetDone}
                      text={
                        'I confirm that I have backed up my personal information, wiped and reset my device to factory settings.'
                      }
                    />
                  </div>
                </div>
              )}
            </FormContainer>
          </div>

          <div className="bg-gray-100 rounded">
            <FormContainer header="Your New Purchased Device">
              <DisabledField id="purchased-vendor" label="Vendor" value="Samsung" />
              <DisabledField id="purchased-model" label="Model Purchased" value={purchasedModel || ''} />
              <DisabledField id="purchased-date" label="Date Purchased" value={getPurchasedDate(purchaseDate)} />
              <DisabledField id="purchased-imei" label="IMEI of Purchased Device" value={imei} />
            </FormContainer>
          </div>

          <div className="bg-gray-100 rounded">
            <FormContainer header="Trade-in Total">
              {/* purchasedPhone && qualifyingDevices.includes(purchasedPhone) && ( */}
              {purchasedPhone && (
                <>
                  {tradeInPromoValue !== 0 && (
                    <DisabledField
                      id="final-trade-in-value"
                      label="Trade-in Value of Old Device"
                      value={`€ ${tradeInValueFinal}`}
                    />
                  )}
                  {tradeInPromoValue !== 0 && (
                    <>
                      <DisabledField
                        id="final-trade-in-bonus"
                        label="Samsung enhanced trade-in offer"
                        value={`€ ${tradeInPromoValue.toFixed(2)}`}
                      />
                      <br />
                    </>
                  )}
                </>
              )}
              <DisabledField
                id="final-trade-in-total"
                label={
                  //purchasedPhone && qualifyingDevices.includes(purchasedPhone)
                  purchasedPhone ? 'Total Trade-in Value' : 'Samsung Reward'
                }
                value={`€ ${tradeInValueTotal.toFixed(2)}`}
                inputClasses={styles.TradeInTotalPrice}
              />
            </FormContainer>
          </div>
        </form>
      </div>
    </Step>
  );
});

export default OldDeviceStatus;
